import { ajvResolver } from '@hookform/resolvers/ajv';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { JSONSchemaType } from 'ajv';
import { useMemo } from 'react';
import { type Path, Controller, SubmitHandler, useForm } from 'react-hook-form';
import { SidebarAccordion } from 'src/components/accordion';
import { useEditComponentPageContext } from 'src/components/context/components/EditComponentPageContext';
import { Drawer } from 'src/components/drawer';
import { SidebarHeader } from 'src/components/header';
import { ResourceSelectorSidebar } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget';
import { ResourceType } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/ResourceSelectorSidebar.interface';
import { useGetThemes } from 'src/hooks';
import { type IGcfComponentConfig } from 'src/interfaces';
import { lang } from 'src/utils';
import { handleComponentConfigChange } from 'src/utils/changeHandler';

import { CommonFormProps } from '../FormSelector.interface';
import { FormWrapper } from '../FormWrapper';
import { ComponentSettingAccordion } from '../modules/componentSettingAccordion/ComponentSettingAccordion';
import { HeaderTextStylingAccordion } from '../modules/headerTextStylingAccordion/HeaderTextStylingAccordion';
import { ProductsDrawer } from '../modules/productsDrawer';
import {
  ComboStyleConfigInterface,
  ComboStyleConfigSchema,
} from './ComboStyle.interface';
import { useComboStyleForm } from './ComboStyleForm.hook';
import { SidebarContainer, SidebarContent } from './ComboStyleForm.styled';

export const ComboStyleForm = (props: CommonFormProps) => {
  const formValues = useMemo(
    () => structuredClone(props.componentDetails.config),
    [props.componentDetails.config],
  );

  const {
    control,
    handleSubmit: submitWrapper,
    reset,
    formState,
    getValues,
  } = useForm<IGcfComponentConfig>({
    values: formValues,
    resolver: ajvResolver(
      ComboStyleConfigSchema as unknown as JSONSchemaType<ComboStyleConfigInterface>,
    ),
  });

  const {
    openBackgroundDrawer,
    openTitleDrawer,
    openSettingsDrawer,
    openProductsDrawer,
    onToggleBackgroundDrawer,
    onToggleTitleDrawer,
    onToggleSettingsDrawer,
    onToggleProductsDrawer,
  } = useComboStyleForm();

  const { data: themes = [] } = useGetThemes();
  const { handleChangeComponentDetails } = useEditComponentPageContext();

  const handleSubmit: SubmitHandler<IGcfComponentConfig> = (
    value: typeof props.componentDetails.config,
  ) => {
    props.onSave(value);
  };

  const handleOnChange = (
    key: Path<IGcfComponentConfig> | Path<IGcfComponentConfig>[],
    value: unknown,
    fieldOnChange?: any,
  ) => {
    props.onChange(props.componentDetails.config ?? {}, key, value);
    fieldOnChange && fieldOnChange(value);
  };

  const handleMultipleChange = (
    keys: Path<IGcfComponentConfig>[],
    value: unknown | unknown[],
    fieldOnChange?: (...event: any[]) => void,
  ) => {
    props.onChange(props.componentDetails.config ?? {}, keys, value);
    if (fieldOnChange) {
      Array.isArray(value) ? fieldOnChange(value[0]) : fieldOnChange(value);
    }
  };

  const headerTextStyleHasErrors = () => {
    if (
      formState.errors.header ||
      formState.errors.disclaimer ||
      formState.errors.productList?.config?.sizeLabel ||
      formState.errors.productList?.config?.sizes
    )
      return true;
    else return false;
  };

  const settingsHasErrors = () => {
    if (
      formState.errors.productList?.config?.badges?.groupBadge ||
      formState.errors.productList?.config?.badges?.itemBadge
    )
      return true;
    else return false;
  };

  const handleThemeChange = (themeId: unknown) => {
    const theme = themes.find((theme) => theme.id === themeId);

    if (theme) {
      handleChangeComponentDetails([themeId, theme], ['theme_id', 'theme']);
    }
  };
  const hasBackgroundImage = (mop?: { background?: string }) =>
    typeof mop?.background === 'string' && mop.background.trim() !== '';

  const items = [
    {
      label: lang.componentSettings,
      onLabelClick: onToggleSettingsDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: settingsHasErrors(),
    },
    {
      label: lang.background,
      onLabelClick: onToggleBackgroundDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: props.hasTriedSaving
        ? !hasBackgroundImage(props.componentDetails.config)
        : false,
    },
    {
      label: lang.headerTextStyling,
      onLabelClick: onToggleTitleDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: headerTextStyleHasErrors(),
    },
    {
      label: lang.products,
      onLabelClick: onToggleProductsDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: false,
    },
  ];

  return (
    <FormWrapper
      formState={formState}
      formReset={reset}
      formGetValues={getValues}
      onSave={submitWrapper(handleSubmit)}>
      <SidebarAccordion items={items} />
      <Controller
        name="background"
        control={control}
        render={({ field: { ref, ...field }, fieldState }) => (
          <ResourceSelectorSidebar
            label={lang.background}
            ref={ref}
            type={ResourceType.BACKGROUND}
            defaultAssetName={field.value}
            openResourceSelectorDrawer={openBackgroundDrawer}
            onToggleResourceSelectorDrawer={onToggleBackgroundDrawer}
            onChange={(value) =>
              handleComponentConfigChange(
                props,
                'background',
                value,
                field.onChange,
              )
            }
            hasError={!!fieldState.error}
            error={fieldState.error?.message || false}
          />
        )}
      />

      <Drawer open={openTitleDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.headerTextStyling}
            onBackClick={onToggleTitleDrawer}
          />
          <SidebarContent>
            <HeaderTextStylingAccordion
              componentConfig={props.componentDetails.config}
              handleOnChange={handleOnChange}
              errors={formState?.errors}
              enableIndividualStyles={true}
              theme={props.componentDetails.theme}
            />
          </SidebarContent>
        </SidebarContainer>
      </Drawer>

      <Drawer open={openSettingsDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.componentSettings}
            onBackClick={onToggleSettingsDrawer}
          />
          <SidebarContent>
            <ComponentSettingAccordion
              config={props.componentDetails.config}
              currentTheme={props.componentDetails.theme}
              options={{ showBadgesController: true, showColumns: true }}
              handleChange={handleOnChange}
              handleThemeChange={handleThemeChange}
            />
          </SidebarContent>
        </SidebarContainer>
      </Drawer>

      <ProductsDrawer
        open={openProductsDrawer}
        componentDetails={props.componentDetails}
        handleOnChange={handleMultipleChange}
        onClose={onToggleProductsDrawer}
      />
    </FormWrapper>
  );
};
